import { Button, Loader } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import {
  fetchSurveyResponses,
  getFetchSurveyResponsesStatus,
  getPostResponsesStatus,
  getResetAnswersStatus,
  getSurveyResponses,
  getSurveyResponsesCompleted,
  postResponses,
  resetAnswers,
  resetQuestion,
} from '../../surveySlice';
import ResponseSection from '../ResponseSection/ResponseSection';

import './ReviewResponses.scss';
import { formatResultsRoute } from '../../../features/divestitureDiagnostic/routes';
import {
  fetchDivestitureDiagnosticResults,
  getCurrentContentTypeFromURL,
} from '../../../features/divestitureDiagnostic/divestitureDiagnosticSlice';
import userTokenService from '../../../common/services/security/userTokenService';
import { CONTENT_TYPE_SURVEY } from '../../routes';
import AdditionalCommentsSection from './AdditionalCommentsSection/AdditionalCommentsSection';
import featureFlags from '../../../common/helpers/featureFlags';

const formatAnswer = (answers, section, question) => {
  const formatRadioAnswer = (foundAnswer) => {
    const foundOption = question.options.find(
      (option) => option.id === foundAnswer.optionId
    );
    return {
      ...foundAnswer,
      text: foundOption && foundOption.text,
    };
  };
  const formatTextAnswer = (foundAnswer) => ({
    ...foundAnswer,
    text: foundAnswer.value,
  });
  const formatMultiselectAnswer = (foundAnswer) =>
    foundAnswer.map((answer) => {
      const foundOption = question.options.find(
        (option) => option.id === answer.optionId
      );
      return {
        ...answer,
        text: foundOption && foundOption.text,
      };
    });

  const formatFoundAnswer = (ans) => {
    switch (question.type) {
      case 'radio':
        return formatRadioAnswer(ans);
      case 'percent':
        return formatTextAnswer(ans);
      case 'multiselect':
        return formatMultiselectAnswer(ans);
      default:
        return formatTextAnswer(ans);
    }
  };

  const foundAnswer = answers[
    question.type === 'multiselect' ? 'filter' : 'find'
  ](
    (answer) =>
      answer.sectionId === section.id && answer.questionId === question.id
  );

  if (foundAnswer) {
    return formatFoundAnswer(foundAnswer);
  }
  return null;
};

export const formatSurveyResponses = (surveyResponses) =>
  surveyResponses && surveyResponses.sections
    ? surveyResponses.sections.map((section) => ({
        sectionId: section.id,
        sectionPath: section.path,
        sectionTitle: section.title,
        questions: surveyResponses.questions
          .filter((question) => question.sectionId === section.id)
          .map((question) => ({
            ...question,
            id: question.id,
            sectionId: question.sectionId,
            text: question.text,
            answer: formatAnswer(surveyResponses.answers, section, question),
          })),
      }))
    : [];

const ReviewResponses = ({ isSurveySubmitted }) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const history = useHistory();

  const surveyResponses = useSelector(getSurveyResponses);
  const { pending: resetAnswersPending } = useSelector(getResetAnswersStatus);
  const { pending: postResponsesPending, success: postResponsesSuccess } =
    useSelector(getPostResponsesStatus);
  const fetchSurveyResponseStatus = useSelector(getFetchSurveyResponsesStatus);
  const surveyResponsesCompleted = useSelector(getSurveyResponsesCompleted);
  const contentType = useSelector(getCurrentContentTypeFromURL);

  const [isClient, setIsClient] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [additionalComments, setAdditionalComment] = useState('');

  const isSurveyRoute = contentType === CONTENT_TYPE_SURVEY;

  useEffect(() => {
    if (!surveyResponses) {
      dispatch(fetchSurveyResponses(projectId));
    }
  }, [dispatch, projectId, surveyResponses]);

  useEffect(() => {
    if (isSurveyRoute && postResponsesSuccess) {
      dispatch(resetQuestion());
      dispatch(fetchDivestitureDiagnosticResults(projectId));
      history.push(formatResultsRoute(projectId));
    }
  }, [postResponsesSuccess, history, projectId, dispatch, isSurveyRoute]);

  useEffect(() => {
    let isMounted = true;
    const userRole = userTokenService.getProjectTokenProperty(
      projectId,
      'role'
    );
    const approverPermission = userTokenService.hasProjectPermission(
      projectId,
      ['Approve']
    );

    if (isMounted) {
      setIsClient(Constants.USER_ROLE_GROUPS.CLIENT.includes(userRole));
      setHasPermission(approverPermission);
    }

    return () => {
      isMounted = false;
    };
  }, [setIsClient, setHasPermission, projectId, dispatch]);

  return (
    <>
      {surveyResponses && (
        <div className="review-responses-container">
          {surveyResponses.sections &&
            formatSurveyResponses(surveyResponses).map((res) => (
              <ResponseSection
                projectId={projectId}
                section={res}
                key={res.sectionId}
              />
            ))}
          <AdditionalCommentsSection
            additionalComments={additionalComments}
            onChange={(value) => setAdditionalComment(value)}
          />
          {isSurveyRoute && (
            <div className="responses-footer">
              <Button
                className="submit-responses-button"
                onClick={() =>
                  dispatch(postResponses(projectId, additionalComments))
                }
                disabled={
                  !surveyResponsesCompleted ||
                  postResponsesPending ||
                  isSurveySubmitted ||
                  (!isClient && !hasPermission)
                }
              >
                Submit Responses
              </Button>
              {featureFlags.diagnosticDebuggingControls && (
                <Button
                  className="reset-answers-button"
                  onClick={() => dispatch(resetAnswers(projectId))}
                  disabled={resetAnswersPending || isSurveySubmitted}
                >
                  Reset Answers
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {fetchSurveyResponseStatus.pending && <Loader />}
    </>
  );
};

export default ReviewResponses;
